import React, { Component, ReactNode } from 'react';

// Safe React check
const isSafeReact = () => {
  return typeof React !== 'undefined' &&
         typeof React.Component !== 'undefined' &&
         typeof React.createElement !== 'undefined';
};

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorCount: number;
  lastError: string;
}

class GlobalErrorHandler extends Component<Props, State> {
  private errorThrottle: Set<string> = new Set();
  private throttleTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorCount: 0,
      lastError: ''
    };

    // Prevent console spam by throttling repeated errors
    if (typeof window !== 'undefined') {
      this.setupGlobalErrorHandlers();
    }
  }

  setupGlobalErrorHandlers() {
    // Override console.error to prevent spam
    const originalError = console.error;
    console.error = (...args) => {
      const errorMessage = args.join(' ');

      // Throttle specific SSR-related errors
      if (this.shouldThrottleError(errorMessage)) {
        if (!this.errorThrottle.has(errorMessage)) {
          this.errorThrottle.add(errorMessage);
          originalError.apply(console, ['[THROTTLED]', ...args]);

          // Clear throttle after 5 seconds
          if (this.throttleTimeout) clearTimeout(this.throttleTimeout);
          this.throttleTimeout = setTimeout(() => {
            this.errorThrottle.clear();
          }, 5000);
        }
        return;
      }

      originalError.apply(console, args);
    };

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const errorMessage = event.reason?.message || 'Unknown promise rejection';
      if (this.shouldThrottleError(errorMessage)) {
        event.preventDefault();
        console.warn('[THROTTLED] Unhandled promise rejection:', errorMessage);
      }
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
      const errorMessage = event.message;
      if (this.shouldThrottleError(errorMessage)) {
        event.preventDefault();
        console.warn('[THROTTLED] Global error:', errorMessage);
      }
    });
  }

  shouldThrottleError(message: string): boolean {
    const throttledPatterns = [
      'Cannot read properties of null',
      'Cannot read properties of undefined',
      'Cancel rendering route',
      'client-side exception has occurred',
      'useWrappedStore',
      'useMemo',
      'HYDRATION',
      'Hydration failed',
      'reading \'favicon\'',
      'TypeError: Cannot read properties'
    ];

    return throttledPatterns.some(pattern =>
      message.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  static getDerivedStateFromError(error: Error): State {
    console.warn('GlobalErrorHandler caught error:', error.message);

    return {
      hasError: true,
      errorCount: 1,
      lastError: error.message
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details but don't spam console
    console.group('🚨 Error Boundary Caught Error');
    console.warn('Error:', error.message);
    console.warn('Component Stack:', errorInfo.componentStack);
    console.groupEnd();

    // Auto-recover from SSR-related errors
    if (this.isSSRError(error.message)) {
      setTimeout(() => {
        this.setState({ hasError: false, errorCount: 0 });
      }, 1000);
    }
  }

  isSSRError(message: string): boolean {
    const ssrErrorPatterns = [
      'Cannot read properties of null',
      'Cannot read properties of undefined',
      'useWrappedStore',
      'useMemo',
      'Cancel rendering route',
      'reading \'favicon\'',
      'getInitialProps'
    ];

    return ssrErrorPatterns.some(pattern =>
      message.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  render() {
    // Check if React is safe to use
    if (!isSafeReact()) {
      // Fallback to plain DOM if React is broken
      if (typeof window !== 'undefined') {
        document.body.innerHTML = `
          <div style="display: flex; justify-content: center; align-items: center; height: 100vh; flex-direction: column;">
            <div>React Loading Error</div>
            <div style="font-size: 12px; color: #666; margin-top: 10px;">
              Reloading page...
            </div>
          </div>
        `;
        setTimeout(() => window.location.reload(), 2000);
      }
      return null;
    }

    if (this.state.hasError) {
      // For SSR errors, show a minimal loading state
      if (this.isSSRError(this.state.lastError)) {
        return React.createElement('div', {
          style: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column'
          }
        }, [
          React.createElement('div', { key: 'loading' }, 'Loading...'),
          React.createElement('div', {
            key: 'subtitle',
            style: { fontSize: '12px', color: '#666', marginTop: '10px' }
          }, 'Initializing application...')
        ]);
      }

      // For other errors, show error message
      return React.createElement('div', {
        style: {
          padding: '20px',
          margin: '20px',
          border: '1px solid #ff6b6b',
          borderRadius: '4px',
          backgroundColor: '#ffe0e0'
        }
      }, [
        React.createElement('h3', { key: 'title' }, 'Something went wrong'),
        React.createElement('p', { key: 'message' }, `Error: ${this.state.lastError}`),
        React.createElement('button', {
          key: 'reload',
          onClick: () => window.location.reload(),
          style: {
            padding: '8px 16px',
            backgroundColor: '#ff6b6b',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }
        }, 'Reload Page')
      ]);
    }

    return this.props.children;
  }
}

export default GlobalErrorHandler;